import { addDays, parseISO, formatISO } from 'date-fns';

import type { ChecklistTemplate } from '@/types/checklist-template';
import type { Season } from '@/types/season';
import type { Property } from '@/types/property';
import type { Task, CreateTaskInput } from '@/types/task';
import { TimingAnchor } from '@/types/task';
import type { TaskService } from './task-service';
import { ZONE_CONFIG, getZoneConfig, computeZoneWindowOffset, selectZoneWindowKey } from '@/data/zone-data';
import { validateDependencies } from './dependency-utils';

// Northern Midwest is the template authoring zone — all dates are defined relative to it
const NORTHERN_ZONE = ZONE_CONFIG['northern_midwest'];

// M231 (issue #1318): month at/after which a frost/rut reference date belongs to "the fall
// half" of a hunting-season cycle. No production template opens in Aug (earliest tested opener
// is Sept 13), so this boundary cannot regress any existing scenario; it only activates for
// openers before Aug combined with a frost/rut event at/after Aug.
const FALL_CYCLE_THRESHOLD_MONTH = 8;

/**
 * Resolve a calendar-date task, optionally shifting by zone offset.
 * Zone offset is computed from the agronomic window (frost-seed, fall-clover, or
 * fall-brassica) whose Northern Midwest reference month is closest to this task's own
 * calendarMonth (M231, issue #1318) — not always fallBrassicaWindow.
 */
function resolveCalendarDate(
  month: number,
  day: number,
  year: number,
  zone: string | undefined,
  zoneAdjustable: boolean,
): string {
  const baseDate = new Date(year, month - 1, day);
  if (!zoneAdjustable || !zone || zone === 'northern_midwest') {
    return formatISO(baseDate, { representation: 'date' });
  }
  const targetConfig = getZoneConfig(zone);
  if (!targetConfig) {
    return formatISO(baseDate, { representation: 'date' });
  }
  const windowKey = selectZoneWindowKey(month);
  const offsetDays = computeZoneWindowOffset(NORTHERN_ZONE[windowKey], targetConfig[windowKey]);
  return formatISO(addDays(baseDate, offsetDays), { representation: 'date' });
}

/**
 * Resolves which calendar year a fall-cycle event (frost/rut) belongs to, relative to the
 * season's opener. Handles openers early in the calendar year (e.g. a January muzzleloader
 * opener) whose "fall half" (frost/rut) events belong to the PRIOR calendar year, not the
 * opener's own year (M231, issue #1318).
 */
function resolveCycleYear(eventMonth: number, openerYear: number, openerMonth: number): number {
  const eventIsFallCycle = eventMonth >= FALL_CYCLE_THRESHOLD_MONTH;
  const openerIsEarlyYear = openerMonth < FALL_CYCLE_THRESHOLD_MONTH;
  return eventIsFallCycle && openerIsEarlyYear ? openerYear - 1 : openerYear;
}

/**
 * Resolve a frost-relative task.
 * Negative offsetDays = before average first frost; positive = after average first frost.
 * `openerMonth` corrects the cycle year for early-year openers (M231, issue #1318).
 */
function resolveFrostDate(
  zone: string | undefined,
  offsetDays: number,
  openerYear: number,
  openerMonth: number,
): string {
  const config = zone ? getZoneConfig(zone) : null;
  const frostMonth = config?.averageFirstFrostMonth ?? NORTHERN_ZONE.averageFirstFrostMonth;
  const frostDay = config?.averageFirstFrostDay ?? NORTHERN_ZONE.averageFirstFrostDay;
  const year = resolveCycleYear(frostMonth, openerYear, openerMonth);
  const frostDate = new Date(year, frostMonth - 1, frostDay);
  return formatISO(addDays(frostDate, offsetDays), { representation: 'date' });
}

/**
 * Resolve a rut-relative task.
 * offsetDays = 0 means rut peak day; negative = before peak; positive = after peak.
 * `openerMonth` corrects the cycle year for early-year openers (M231, issue #1318).
 */
function resolveRutRelativeDate(
  zone: string | undefined,
  offsetDays: number,
  openerYear: number,
  openerMonth: number,
): string {
  const config = zone ? getZoneConfig(zone) : null;
  const rutMonth = config?.rutPeakMonth ?? NORTHERN_ZONE.rutPeakMonth;
  const rutDay = config?.rutPeakDay ?? NORTHERN_ZONE.rutPeakDay;
  const year = resolveCycleYear(rutMonth, openerYear, openerMonth);
  const rutDate = new Date(year, rutMonth - 1, rutDay);
  return formatISO(addDays(rutDate, offsetDays), { representation: 'date' });
}

/**
 * ChecklistEngine generates concrete Tasks from a ChecklistTemplate + Season + optional Property.
 *
 * Zone-aware: calendar_date tasks with zoneAdjustable=true shift by the difference between
 * the property's zone and the Northern Midwest reference zone, using whichever of the three
 * agronomic windows (frost-seed, fall-clover, fall-brassica) is closest to the task's own
 * calendarMonth (M231, issue #1318). frost_date/rut_relative tasks additionally resolve to the
 * correct hunting-season cycle year even when the season's opener falls early in the calendar
 * year (M231, issue #1318) — calendar_date tasks are unaffected by this year correction.
 */
export class ChecklistEngine {
  constructor(private taskService: TaskService) {}

  async generateFromTemplate(
    template: ChecklistTemplate,
    season: Season,
    property?: Property,
    sortOrderOffset = 0,
  ): Promise<Task[]> {
    const openerDate = parseISO(season.openerDate);
    const year = openerDate.getFullYear();
    const openerMonth = openerDate.getMonth() + 1; // M231 (issue #1318): fall-cycle year correction
    const zone = property?.zone;

    // Validate dependency graph before allocating any IDs
    const depValidation = validateDependencies(
      template.tasks.map((t) => ({ id: t.id, dependsOn: t.dependsOn })),
    );
    if (!depValidation.valid) {
      throw new Error(
        `Circular dependency in template "${template.name}": ${depValidation.cycle?.join(' → ')}`,
      );
    }

    // Pre-assign stable UUIDs so dependency chains can reference sibling tasks
    const idMap = new Map<string, string>();
    template.tasks.forEach((tt) => idMap.set(tt.id, crypto.randomUUID()));

    const inputs: CreateTaskInput[] = template.tasks.map((tt, index) => {
      const generatedId = idMap.get(tt.id)!; // SAFETY: idMap is populated for every task above

      let dueDate: string;
      switch (tt.timingAnchor) {
        case TimingAnchor.CalendarDate:
          dueDate = resolveCalendarDate(
            tt.calendarMonth ?? 1,
            tt.calendarDayRangeStart ?? 1,
            year,
            zone,
            tt.zoneAdjustable,
          );
          break;
        case TimingAnchor.FrostDate:
          dueDate = resolveFrostDate(zone, tt.offsetDays, year, openerMonth);
          break;
        case TimingAnchor.RutRelative:
          dueDate = resolveRutRelativeDate(zone, tt.offsetDays, year, openerMonth);
          break;
        case TimingAnchor.OpenerRelative:
        default:
          dueDate = formatISO(addDays(openerDate, tt.offsetDays), { representation: 'date' });
          break;
      }

      const dependsOn = tt.dependsOn
        .map((depId) => idMap.get(depId))
        .filter((d): d is string => d !== undefined);

      return {
        id: generatedId,
        templateTaskId: tt.id,
        templateId: template.id,
        seasonId: season.id,
        propertyId: tt.propertyTied ? property?.id : undefined,
        title: tt.title,
        description: tt.description,
        category: tt.category,
        selectedAction: '',
        dueDate,
        timingAnchor: tt.timingAnchor,
        offsetDays: tt.offsetDays,
        dependsOn,
        blocked: dependsOn.length > 0,
        completed: false,
        sortOrder: (tt.sortOrder ?? index) + sortOrderOffset,
        notes: tt.notes ?? '',
      };
    });

    return this.taskService.bulkCreateTasks(inputs);
  }
}
